/**
 * 匹配订单所在门店
 */
import React, { useMemo } from 'react';
import { matchPath } from 'react-router-dom';
import { get } from 'lodash';
import intl from 'react-intl-universal';
import { message } from 'antd';

import Storage from '@/utils/storage';
import { getUserInfoWithCache } from '@/api/cacheRequest';
import { getOrderBaseInfo } from '@/api/order';
import usePortalConfirm from '@/components/PortalConfirm';
import { OrderDetailPageList } from '@/hooks/useOrderLink';
import { OrderType } from '@/config/constant';

import styles from './index.module.scss';

interface StoreInfo {
  commonCode: string;
  shopName: string;
}

let UserInfoCache: Promise<{ shopList: { commonCode: string }[] }>;
const getCurrentUserWithCache = () => {
  if (UserInfoCache) return UserInfoCache;
  UserInfoCache = getUserInfoWithCache()
    .then((res) => {
      return {
        // 当前用户门店列表
        shopList: get(res, 'data.shops'),
      };
    })
    .catch(() => {
      return {
        shopList: null,
      };
    });
  return UserInfoCache;
};

const Index = () => {
  const confirmPortal = usePortalConfirm();
  const { detectorQueue, queueResolver } = useMemo(() => {
    let asyncResolver: (val: boolean) => any;
    const asyncQueue = new Promise<boolean>((resolve) => {
      asyncResolver = resolve;
    });
    return { detectorQueue: asyncQueue, queueResolver: asyncResolver };
  }, []);

  // 终止
  const handleConfirmAbort = () => {
    // 关闭弹窗，停留在当前门店 并跳转到房态页面【首页】
    window.location.replace('/');
    confirmPortal.closeDrawer();
    queueResolver(false);
  };

  // 无权限订单
  const showNoPermission = (store: StoreInfo) => {
    confirmPortal.showDrawer({
      title: intl.get('cmp_order_detail_portal_detect_title'),
      content: (
        <div className={styles.messageWrapper}>
          {intl.getHTML('cmp_order_detail_portal_detect_content_auth', {
            store: `${store.commonCode}${store.shopName}`,
          })}
        </div>
      ),
      confirmText: intl.get('app_confirm_confirm_btn'),
      onCancel: handleConfirmAbort,
      onConfirm: handleConfirmAbort,
    });
  };

  // 门店错误订单
  const showSwitchConfirm = (store: StoreInfo) => {
    confirmPortal.showDrawer({
      title: intl.get('cmp_order_detail_portal_detect_title'),
      content: (
        <div className={styles.messageWrapper}>
          {intl.getHTML('cmp_order_detail_portal_detect_content_switch', {
            store: `${store.commonCode}${store.shopName}`,
          })}
        </div>
      ),
      cancelText: intl.get('app_confirm_cancel_btn'),
      confirmText: intl.get('app_confirm_confirm_btn'),
      onConfirm: async () => {
        // 更新本地门店码
        Storage.set('commonCode', store.commonCode);
        // 清除集团标识
        Storage.delete('isGroupLogin');
        window.location.reload();
        queueResolver(false);
      },
      onCancel: handleConfirmAbort,
    });
  };

  // 根据路由名称查询菜单
  const findMenuByRoute = (route: string, menuList) => {
    return menuList.find((menu) => {
      if (menu.path === route) {
        return true;
      }
      // 匹配子菜单
      if (menu.children && menu.children.length) {
        return findMenuByRoute(route, menu.children);
      }
      return false;
    });
  };

  // 获取详细信息
  const startDetailDetector = async (orderType: OrderType) => {
    try {
      // 匹配订单类型配置
      const matchItem = OrderDetailPageList.find((item) => item.type === orderType);
      if (!matchItem) throw new Error('ORDER_TYPE_NULL');
      // 匹配订单号
      const orderNo = matchPath(window.location.pathname, { path: matchItem.route, exact: true })
        .params[matchItem.paramKey];
      if (!orderNo) throw new Error('ORDER_NO_NULL');
      // 获取门店权限和订单信息
      const [userInfo, orderInfo] = await Promise.all([
        getCurrentUserWithCache(),
        getOrderBaseInfo({
          orderNo,
          orderType: matchItem.bizCode,
        })
          .then((res) => {
            if (res?.data?.extensionToAnther) message.warning('该订单已被续住/联房');
            return res.data;
          })
          .catch(() => null),
      ]);
      const targetStore = orderInfo.commonCode;
      // 订单所属门店与当前门店一致
      if (Storage.get('commonCode') === targetStore) {
        queueResolver(true);
        return;
      }
      // 有订单所在门店的权限
      if (userInfo.shopList.some((item) => item.commonCode === targetStore)) {
        showSwitchConfirm(orderInfo);
      } else {
        // 无权限
        showNoPermission(orderInfo);
      }
      return;
    } catch (err) {
      console.warn('Match_ORDER_ERR', err.message);
    }
    // 默认不继续
    queueResolver(false);
  };

  // 获取订单信息
  const runDetailDetector = (orderType: OrderType) => {
    startDetailDetector(orderType);
    return detectorQueue;
  };

  return { runDetailDetector };
};

export default Index;
